// chat/photo.tsx — the camera button, twin of the mic (HARD GATE). Cloned from
// study_companion (the fleet reference implementation).
//
// Bogdan's Interview review: "no option to add info about the company, take photos, feed
// the AI about it, get a structured understanding about the interview we prepare for."
//
// The photographed job posting is read by **on-device Apple Vision OCR** (`store.ocrImage`)
// and only the RECOGNIZED TEXT is handed back (into the briefing box or the chat turn). The
// image bytes never leave the phone and never reach the model — so the photo itself is FREE,
// and it composes with the byte-path rule (CUSTOMER-CLOUD-STORAGE.md → THE BYTE PATH).
//
// ONE <input accept="image/*"> gives iOS the native "Take Photo / Photo Library" sheet, so a
// single control covers both. `capture` is deliberately NOT set: setting it forces the camera
// and removes the library option.
import { useRef, useState } from "react";
import { platform } from "../tauri-web";
import { open as openFileDialog } from "@tauri-apps/plugin-dialog";
import { store } from "../store";

// Byte-identical to voice.tsx's MIC so the two buttons are visually twins.
const ICON_BTN: React.CSSProperties = {
  flexShrink: 0, width: 40, height: 40, borderRadius: 999, padding: 0, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "1px solid var(--border, #d9d4ea)", background: "var(--elev-2, #ffffff)",
  color: "var(--ink-1, #1f1b2e)", fontSize: 18, lineHeight: 1,
  transition: "background .15s, color .15s, box-shadow .15s, border-color .15s",
};

/**
 * Photograph a page → on-device OCR → hand the text back.
 *
 * `onText` receives the recognized text. `onNotice` reports a FAILURE, and the caller must put it
 * in the conversation — an error about the chat belongs IN the chat. It used to render as a line
 * inside the composer, which (a) read as if it came from outside the conversation and (b) grew the
 * composer by a row, permanently shrinking the message list, because no failure path ever cleared
 * it. Progress needs no message at all: the button shows "…" while `busy`.
 */
export function PhotoButton({
  onText, onNotice, disabled,
}: { onText: (text: string) => void; onNotice: (msg: string) => void; disabled?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function readPage(path: string) {
    setBusy(true);
    try {
      let text = "";
      try { text = (await store.ocrImage(path)).trim(); }
      catch { onNotice("I can't scan images on this device — paste or type the text instead and I'll work from that."); return; }
      if (!text) { onNotice("I couldn't read any text on that page. Try a clearer, straight-on photo — or paste the text and I'll work from that."); return; }
      onText(text);
    } finally { setBusy(false); }
  }

  async function pick() {
    // Only the DESKTOP shells have a native file dialog. On iOS openFileDialog is a no-op, and on
    // the web surface the Tauri dialog plugin doesn't exist at all — both use the hidden <input>.
    // platform() is sync, so .click() stays inside the tap's user-gesture (required by WebKit).
    const p = platform();
    const desktop = p === "macos" || p === "windows" || p === "linux";
    if (!desktop) { inputRef.current?.click(); return; }
    try {
      const picked = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "heic", "webp"] }] });
      if (picked && typeof picked === "string") await readPage(picked);
    } catch { /* user cancelled */ }
  }

  async function onInputChange(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = ""; // reset so re-picking the same photo refires
    if (!file) return;
    let path: string | null = null;
    try {
      const ext = (file.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const bytes = Array.from(new Uint8Array(await file.arrayBuffer()));
      path = await store.savePagePhoto(bytes, ext);
      await readPage(path);
    } catch {
      onNotice("I couldn't open that photo. Try another one, or paste the text instead.");
    } finally {
      // The saved copy was ONLY the OCR input — remove it, or every photographed posting
      // accumulates forever in app data. Applies ONLY here (this path wrote the copy); the
      // desktop dialog path reads the USER'S own file, which is never deleted.
      if (path) store.deletePagePhoto(path).catch(() => { /* best-effort cleanup */ });
    }
  }

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" onChange={onInputChange} style={{ display: "none" }} />
      <button type="button" style={ICON_BTN} onClick={pick} disabled={disabled || busy}
        aria-label="Photograph a job posting" title="Photograph a job posting or your notes — read on your device; only the text is used">
        {busy ? "…" : "📷"}
      </button>
    </>
  );
}
